import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const bracketHighlightPluginKey = new PluginKey('bracket-highlight');

export interface BracketMatch {
  from: number;
  to: number;
  text: string;
}

export type OnBracketClick = (match: BracketMatch, event: MouseEvent) => void;

/**
 * Creates a ProseMirror plugin that highlights text between square brackets [ text ] in blue
 * Similar styling to misspelling plugin with background color and pointer cursor
 */
export function createBracketHighlightPlugin(onBracketClick?: OnBracketClick) {
  // Regex pattern to match text between square brackets: [ text ]
  // Matches: [ followed by one or more non-] characters, followed by ]
  const regex = /\[[^\]]+\]/g;

  // Function to find all bracket matches in the document
  function findMatches(doc: any): Array<BracketMatch> {
    const matches: Array<BracketMatch> = [];
    
    doc.descendants((node: any, pos: number) => {
      if (node.isText && node.text) {
        let match;
        const text = node.text;
        
        // Reset regex for each text node
        regex.lastIndex = 0;
        
        while ((match = regex.exec(text)) !== null) {
          const from = pos + match.index;
          const to = from + match[0].length;
          matches.push({ from, to, text: match[0] });
        }
      }
    });
    
    return matches;
  }

  // Create decorations for all matches
  function createDecorations(doc: any): DecorationSet {
    const matches = findMatches(doc);
    const decorations = matches.map(match => {
      // Create an inline decoration with styling similar to misspelling plugin
      return Decoration.inline(match.from, match.to, {
        class: 'bracket-highlight',
        style: 'background-color: #E3F2FD; cursor: pointer;',
        'data-bracket-text': match.text,
        'data-clickable': 'true',
      });
    });
    
    return DecorationSet.create(doc, decorations);
  }

  return new Plugin({
    key: bracketHighlightPluginKey,
    state: {
      init(_, { doc }) {
        return createDecorations(doc);
      },
      apply(tr, old) {
        // If the document changed, recreate decorations
        if (tr.docChanged) {
          return createDecorations(tr.doc);
        }
        // Otherwise, just map the old decorations through the transaction
        return old.map(tr.mapping, tr.doc);
      }
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
      handleClick(view, pos, event) {
        // Check if the click was on a highlighted bracket
        const target = event.target as HTMLElement;
        
        // Check if we clicked on an element with our decoration
        if (target && target.closest('[data-clickable="true"]')) {
          const bracketElement = target.closest('[data-bracket-text]') as HTMLElement;
          if (bracketElement) {
            const bracketText = bracketElement.getAttribute('data-bracket-text');
            if (bracketText && onBracketClick) {
              // Find the match for this bracket
              const decorations = bracketHighlightPluginKey.getState(view.state);
              if (decorations) {
                const found = decorations.find(pos, pos);
                if (found.length > 0) {
                  const decoration = found[0];
                  const match: BracketMatch = {
                    from: decoration.from,
                    to: decoration.to,
                    text: bracketText,
                  };
                  
                  event.preventDefault();
                  event.stopPropagation();
                  onBracketClick(match, event);
                  return true; // Handled the event
                }
              }
            }
          }
        }
        
        // Alternative: Check decorations at click position
        const decorations = bracketHighlightPluginKey.getState(view.state);
        if (decorations) {
          const found = decorations.find(pos, pos);
          if (found.length > 0) {
            const decoration = found[0];
            const doc = view.state.doc;
            const bracketText = doc.textBetween(decoration.from, decoration.to);
            
            if (bracketText && onBracketClick) {
              const match: BracketMatch = {
                from: decoration.from,
                to: decoration.to,
                text: bracketText,
              };
              
              event.preventDefault();
              event.stopPropagation();
              onBracketClick(match, event);
              return true; // Handled the event
            }
          }
        }
        
        return false; // Let other handlers process this
      }
    }
  });
}

